import { useState, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
} from '@mui/material';
import type { FormDefinition } from './types';
import { buildFormGenerationPrompt } from './buildFormGenerationPrompt';
import { generateFormWithAI } from './generateFormWithAI';
import { parseCSVFile, parseExcelFile } from './spreadsheetParser';
import {
  extractTextFromPDF,
  extractTextFromImage,
  extractTextFromWord,
  isPDFFile,
  isImageFile,
  isWordFile,
} from './pdfImageParser';

interface AIFormGeneratorProps {
  onGenerated: (formDefinition: FormDefinition, replace: boolean) => void;
  existingFieldsCount: number;
}

type InputMethod = 'text' | 'upload';

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_EXTRACTED_TEXT_LENGTH = 800000;

const progressSteps = [
  'Analyzing your request...',
  'Understanding form requirements...',
  'Generating form structure...',
  'Creating field definitions...',
  'Determining field types and options...',
  'Organizing fields into sections...',
  'Finalizing form definition...',
];

export const AIFormGenerator = ({
  onGenerated,
  existingFieldsCount,
}: AIFormGeneratorProps) => {
  const [inputMethod, setInputMethod] = useState<InputMethod>('text');
  const [textDescription, setTextDescription] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReplaceAppendDialog, setShowReplaceAppendDialog] = useState(false);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [progressStep, setProgressStep] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [pendingFormDefinition, setPendingFormDefinition] =
    useState<FormDefinition | null>(null);
  const [replaceAppendChoice, setReplaceAppendChoice] = useState<
    'replace' | 'append'
  >('replace');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleGenerate = async () => {
    setError(null);
    setIsLoading(true);
    setShowProgressDialog(true);
    setProgressStep(0);
    setProgressLabel(progressSteps[0]);

    const progressInterval = setInterval(() => {
      setProgressStep((prev) => {
        const next = Math.min(prev + 1, progressSteps.length - 1);
        setProgressLabel(progressSteps[next]);
        return next;
      });
    }, 3000);

    try {
      let spreadsheetData: string | undefined;
      let userInput: string | undefined;

      if (inputMethod === 'text') {
        if (!textDescription.trim()) {
          throw new Error('Please describe the form you want to create');
        }
      } else if (!uploadedFile) {
        throw new Error('Please select a file to upload');
      } else {
        const ext = uploadedFile.name.split('.').pop()?.toLowerCase();
        if (ext === 'csv') {
          spreadsheetData = await parseCSVFile(uploadedFile);
        } else if (ext === 'xlsx' || ext === 'xls') {
          spreadsheetData = await parseExcelFile(uploadedFile);
        } else if (ext === 'pdf' || isPDFFile(uploadedFile)) {
          const extracted = await extractTextFromPDF(uploadedFile);
          if (extracted.length > MAX_EXTRACTED_TEXT_LENGTH) {
            throw new Error('Extracted PDF text is too long; try a simpler form.');
          }
          userInput = `Generate a form based on this existing PDF form:\n\n${extracted}`;
        } else if (ext === 'docx' || isWordFile(uploadedFile)) {
          if (ext === 'doc') {
            throw new Error('Save as .docx; older .doc is not supported.');
          }
          const extracted = await extractTextFromWord(uploadedFile);
          if (extracted.length > MAX_EXTRACTED_TEXT_LENGTH) {
            throw new Error('Extracted document text is too long.');
          }
          userInput = `Generate a form based on this Word document:\n\n${extracted}`;
        } else if (isImageFile(uploadedFile)) {
          setProgressLabel('Extracting text from image (OCR)...');
          const extracted = await extractTextFromImage(uploadedFile);
          if (extracted.length > MAX_EXTRACTED_TEXT_LENGTH) {
            throw new Error('Extracted image text is too long.');
          }
          userInput = `Generate a form based on this form image:\n\n${extracted}`;
        } else {
          throw new Error(
            'Unsupported file type. Use CSV, Excel, .docx, PDF, or an image.',
          );
        }
      }

      if (!userInput) {
        userInput =
          inputMethod === 'text'
            ? textDescription
            : `Generate a form based on this spreadsheet data:\n\n${spreadsheetData}`;
      }

      const prompt = buildFormGenerationPrompt(userInput, spreadsheetData);
      const formDefinition = await generateFormWithAI(prompt);

      clearInterval(progressInterval);
      setShowProgressDialog(false);
      setProgressStep(0);
      setProgressLabel('');

      if (existingFieldsCount > 0) {
        setPendingFormDefinition(formDefinition);
        setShowReplaceAppendDialog(true);
      } else {
        onGenerated(formDefinition, true);
      }
    } catch (err) {
      clearInterval(progressInterval);
      setShowProgressDialog(false);
      setProgressStep(0);
      setProgressLabel('');
      setError(err instanceof Error ? err.message : 'Failed to generate form');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReplaceAppend = () => {
    if (pendingFormDefinition) {
      onGenerated(pendingFormDefinition, replaceAppendChoice === 'replace');
      setPendingFormDefinition(null);
      setShowReplaceAppendDialog(false);
      setReplaceAppendChoice('replace');
      setTextDescription('');
      setUploadedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setError(
          `File is too large (${(file.size / (1024 * 1024)).toFixed(2)} MB). Max 5 MB.`,
        );
        if (fileInputRef.current) fileInputRef.current.value = '';
        setUploadedFile(null);
        return;
      }
      setUploadedFile(file);
      setError(null);
    }
  };

  return (
    <>
      <Box
        sx={{
          mt: 2,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Generate Form with AI (Beta)
        </Typography>

        <Tabs
          value={inputMethod}
          onChange={(_, v) => {
            setInputMethod(v);
            setError(null);
          }}
          sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'uppercase', letterSpacing: '0.04em' } }}
        >
          <Tab label="Text description" value="text" />
          <Tab label="Upload form" value="upload" />
        </Tabs>

        {inputMethod === 'text' && (
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Describe the form"
            placeholder="Describe the form you want to create"
            value={textDescription}
            onChange={(e) => setTextDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
        )}

        {inputMethod === 'upload' && (
          <Box sx={{ mb: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,.docx,.pdf,image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              sx={{ mb: 1 }}
            >
              {uploadedFile ? uploadedFile.name : 'Select file'}
            </Button>
            {uploadedFile && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB — CSV, Excel,
                Word (.docx), PDF, or image. Max 5 MB.
              </Typography>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
          sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}
        >
          {isLoading ? 'Generating…' : 'Generate form'}
        </Button>
      </Box>

      <Dialog open={showProgressDialog} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle>Generating form</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" sx={{ mb: 3, minHeight: 24 }}>
              {progressLabel}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(progressStep / (progressSteps.length - 1)) * 100}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showReplaceAppendDialog}
        onClose={() => {
          setShowReplaceAppendDialog(false);
          setPendingFormDefinition(null);
        }}
      >
        <DialogTitle>Form already has fields</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This definition already has {existingFieldsCount} field(s). How should
            we apply the AI result?
          </Typography>
          <RadioGroup
            value={replaceAppendChoice}
            onChange={(e) =>
              setReplaceAppendChoice(e.target.value as 'replace' | 'append')
            }
          >
            <FormControlLabel
              value="replace"
              control={<Radio />}
              label="Replace existing fields"
            />
            <FormControlLabel
              value="append"
              control={<Radio />}
              label="Append AI fields to existing"
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowReplaceAppendDialog(false);
              setPendingFormDefinition(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleReplaceAppend} variant="contained">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export function AIFormGeneratorWrapper() {
  const { setValue } = useFormContext();
  const formDefinition = useWatch({ name: 'formDefinition' });

  const existingFieldsCount = (() => {
    try {
      const parsed =
        typeof formDefinition === 'string'
          ? JSON.parse(formDefinition)
          : formDefinition;
      return parsed?.fields?.length || 0;
    } catch {
      return 0;
    }
  })();

  const handleGenerated = (next: FormDefinition, replace: boolean) => {
    try {
      let finalDef: FormDefinition;
      if (replace) {
        finalDef = next;
      } else {
        const current =
          typeof formDefinition === 'string'
            ? JSON.parse(formDefinition || '{"fields":[]}')
            : formDefinition || { fields: [] };
        finalDef = {
          ...current,
          fields: [...(current.fields || []), ...(next.fields || [])],
        };
      }
      setValue('formDefinition', JSON.stringify(finalDef), { shouldDirty: true });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <AIFormGenerator
      onGenerated={handleGenerated}
      existingFieldsCount={existingFieldsCount}
    />
  );
}

export function ConditionalAIGenerator() {
  const formCreationType = useWatch({ name: 'formCreationType' });
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const cloneFrom = params.get('cloneFrom');
  const sourceParam = params.get('source');
  const recordFromState = (location.state as { record?: { id?: number } } | null)
    ?.record;
  const isCloning = !!(
    cloneFrom ||
    (recordFromState && recordFromState.id) ||
    sourceParam
  );

  if (isCloning || formCreationType !== 'ai') return null;
  return <AIFormGeneratorWrapper />;
}
